/**
 * Attachment helpers (tracker item 47): the allowed file types (a mirror of
 * backend attachments.file-types.ts), client-side upload pre-checks, rename
 * splitting, sizes and error messages. The pre-checks are a convenience
 * only; the server checks the bytes and stays authoritative.
 */

import axios from 'axios';
import { getErrorCode, getErrorMessage } from './apiError';

/** Largest file the backend accepts (ATTACHMENT_MAX_BYTES, 10 MB). */
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const ATTACHMENT_MAX_MB = ATTACHMENT_MAX_BYTES / (1024 * 1024);
/** Longest stored description (backend ATTACHMENT_DESCRIPTION_MAX). */
export const ATTACHMENT_DESCRIPTION_MAX = 1000;
/** Longest stored filename (backend ATTACHMENT_FILENAME_MAX). */
export const ATTACHMENT_FILENAME_MAX = 255;
/** Default list page size (backend ATTACHMENT_LIST_DEFAULT_LIMIT). */
export const ATTACHMENT_PAGE_SIZE = 25;

/** Short badge shown as the file's type icon. */
export type AttachmentKind = 'IMG' | 'PDF' | 'TXT' | 'CSV' | 'DOC' | 'XLS';

export interface AttachmentFileTypeInfo {
  contentType: string;
  /** Lowercase, no dot */
  extensions: readonly string[];
  isImage: boolean;
  /** The server sends it inline, so the browser can preview it (JPEG / PNG / WebP) */
  inline: boolean;
  kind: AttachmentKind;
}

/** Same order and content as backend ATTACHMENT_FILE_TYPES. */
export const ATTACHMENT_FILE_TYPES: readonly AttachmentFileTypeInfo[] = [
  { contentType: 'image/jpeg', extensions: ['jpg', 'jpeg'], isImage: true, inline: true, kind: 'IMG' },
  { contentType: 'image/png', extensions: ['png'], isImage: true, inline: true, kind: 'IMG' },
  { contentType: 'image/webp', extensions: ['webp'], isImage: true, inline: true, kind: 'IMG' },
  { contentType: 'image/heic', extensions: ['heic', 'heif'], isImage: true, inline: false, kind: 'IMG' },
  { contentType: 'application/pdf', extensions: ['pdf'], isImage: false, inline: false, kind: 'PDF' },
  { contentType: 'text/plain', extensions: ['txt'], isImage: false, inline: false, kind: 'TXT' },
  { contentType: 'text/csv', extensions: ['csv'], isImage: false, inline: false, kind: 'CSV' },
  { contentType: 'application/rtf', extensions: ['rtf'], isImage: false, inline: false, kind: 'DOC' },
  { contentType: 'application/msword', extensions: ['doc'], isImage: false, inline: false, kind: 'DOC' },
  { contentType: 'application/vnd.ms-excel', extensions: ['xls'], isImage: false, inline: false, kind: 'XLS' },
  {
    contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    extensions: ['docx'], isImage: false, inline: false, kind: 'DOC',
  },
  {
    contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extensions: ['xlsx'], isImage: false, inline: false, kind: 'XLS',
  },
  { contentType: 'application/vnd.oasis.opendocument.text', extensions: ['odt'], isImage: false, inline: false, kind: 'DOC' },
  { contentType: 'application/vnd.oasis.opendocument.spreadsheet', extensions: ['ods'], isImage: false, inline: false, kind: 'XLS' },
];

/** Every accepted extension (no dot), in backend order. */
export const ATTACHMENT_EXTENSIONS: readonly string[] = ATTACHMENT_FILE_TYPES.flatMap((type) => type.extensions);

/**
 * The file input's `accept`: every extension plus the image MIME types, so
 * phones offer the photo library and camera as well as files.
 */
export const ATTACHMENT_ACCEPT = [
  ...ATTACHMENT_EXTENSIONS.map((ext) => `.${ext}`),
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
].join(',');

/** Lowercase extension after the last dot ('' when none, or the name starts with its only dot). Same as the backend. */
export function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot > 0 ? filename.slice(dot + 1).toLowerCase() : '';
}

export function fileTypeForExtension(extension: string): AttachmentFileTypeInfo | undefined {
  const ext = extension.toLowerCase();
  return ATTACHMENT_FILE_TYPES.find((type) => type.extensions.includes(ext));
}

export function fileTypeForContentType(contentType: string): AttachmentFileTypeInfo | undefined {
  return ATTACHMENT_FILE_TYPES.find((type) => type.contentType === contentType);
}

/** Badge for the type icon: from the stored content type, else the filename. */
export function attachmentKind(contentType: string, filename = ''): AttachmentKind | 'FILE' {
  return (fileTypeForContentType(contentType) ?? fileTypeForExtension(extensionOf(filename)))?.kind ?? 'FILE';
}

const allowedList = () => ATTACHMENT_EXTENSIONS.map((ext) => `.${ext}`).join(', ');

/**
 * Client-side pre-check of a picked file (null = looks fine). Size and
 * extension only: the server still sniffs the bytes and may answer 415.
 */
export function attachmentFileError(file: Pick<File, 'name' | 'size'>): string | null {
  const ext = extensionOf(file.name);
  if (!ext || !fileTypeForExtension(ext)) {
    return `${file.name}: files of this type are not allowed. Allowed: ${allowedList()}`;
  }
  if (file.size === 0) return `${file.name}: the file is empty`;
  if (file.size > ATTACHMENT_MAX_BYTES) {
    return `${file.name}: the file is larger than the ${ATTACHMENT_MAX_MB} MB limit (${formatFileSize(file.size)})`;
  }
  return null;
}

/** Human file size: bytes, KB or MB (1024-based, like the 10 MB limit). */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Split a filename for the rename field: the editable base name and the
 * fixed extension suffix (with its dot; '' when there is none). Renames
 * keep the extension, which is what the backend requires.
 */
export function splitFilename(filename: string): { base: string; extension: string } {
  const dot = filename.lastIndexOf('.');
  if (dot <= 0) return { base: filename, extension: '' };
  return { base: filename.slice(0, dot), extension: filename.slice(dot) };
}

/** Rename pre-check: the base name must not be blank, nor make the name too long. */
export function attachmentRenameError(base: string, extension: string): string | null {
  const trimmed = base.trim();
  if (!trimmed || trimmed === '.' || trimmed === '..') return 'A filename is required';
  if (/[\\/]/.test(trimmed)) return 'The filename cannot contain / or \\';
  if (trimmed.length + extension.length > ATTACHMENT_FILENAME_MAX) {
    return `The filename can be at most ${ATTACHMENT_FILENAME_MAX} characters`;
  }
  return null;
}

export function attachmentDescriptionError(description: string): string | null {
  return description.trim().length > ATTACHMENT_DESCRIPTION_MAX
    ? `The description can be at most ${ATTACHMENT_DESCRIPTION_MAX} characters`
    : null;
}

/** The backend's `message` from a JSON error body ('' when there is none, e.g. a proxy's HTML 413). */
function serverMessageOf(err: unknown): string {
  if (!axios.isAxiosError(err)) return '';
  const data = err.response?.data as { message?: unknown } | null | undefined;
  return data && typeof data === 'object' && typeof data.message === 'string' ? data.message : '';
}

/**
 * A clear message for a failed upload. The server's own message wins (it
 * names the reason and the limits); a bare 413 / 415 from a proxy, with no
 * JSON body, still gets a readable explanation.
 */
export function attachmentUploadErrorMessage(err: unknown, filename: string): string {
  const status = axios.isAxiosError(err) ? err.response?.status : undefined;
  const serverMessage = serverMessageOf(err);
  let reason: string;
  if (serverMessage) {
    reason = serverMessage;
  } else if (status === 413) {
    reason = `The file is larger than the ${ATTACHMENT_MAX_MB} MB limit`;
  } else if (status === 415) {
    reason = `Files of this type are not allowed. Allowed: ${allowedList()}`;
  } else {
    reason = getErrorMessage(err, 'Upload failed');
  }
  return `${filename}: ${reason}`;
}

/** A clear message for a failed rename / description edit. */
export function attachmentUpdateErrorMessage(err: unknown, extension: string): string {
  if (getErrorCode(err) === 'FILE_EXTENSION_CHANGE') {
    return serverMessageOf(err) || `The filename must keep its file type (${extension || 'unchanged'})`;
  }
  return getErrorMessage(err, 'Could not save the attachment');
}
